/**
 * AK-423 — Tab 4 «Журнал» (leaf-модель ADR-049).
 *
 * Единая лента (GET /journal): что · когда · кто · было→стало. Фильтр по типу +
 * as-of date-picker (GET /journal/as-of) — снимок поля на дату.
 */
import { useState } from 'react';
import { ArrowRight, CalendarClock, ChevronDown, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import {
  fieldStateLabel,
  JOURNAL_TYPE_LABELS,
  humanizeJournalText,
  isTechnicalReason,
} from './constants';
import {
  useFieldStateLabel,
  type CodeLabeler,
} from '@/lib/reference/fieldCatalogs';
import { useJournal, fetchFieldSnapshot } from './api';
import type { FieldSnapshot, JournalEvent } from './types';

interface Props {
  fieldId: string;
}

const FILTERS: ReadonlyArray<{ value: string | null; label: string }> = [
  { value: null, label: 'Всё' },
  { value: 'rename', label: 'Имя' },
  { value: 'area', label: 'Площадь' },
  { value: 'land_category', label: 'Угодье' },
  { value: 'lifecycle', label: 'Жизненный цикл' },
  { value: 'occupancy', label: 'Посев' },
  { value: 'geometry', label: 'Геометрия' },
  // Пакет 1: правки зон поля раньше не попадали в журнал вовсе.
  { value: 'zone', label: 'Зоны' },
  { value: 'created', label: 'Создание' },
];

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ru-RU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function EventRow({
  ev,
  fallowLabel,
}: {
  ev: JournalEvent;
  /** Подписи состояний поля — из справочника (reference.field_states). */
  fallowLabel: CodeLabeler;
}) {
  const [reasonOpen, setReasonOpen] = useState(false);
  // Эталон founder 2026-07-11: показываем человеческий текст, а не сырые коды
  // («Занятие сезона» → «Посев», «fallow_black» → подпись из справочника).
  const what = humanizeJournalText(ev.what, fallowLabel) ?? ev.what;
  const before = humanizeJournalText(ev.before, fallowLabel);
  const after = humanizeJournalText(ev.after, fallowLabel);
  // Технические причины версий контура («AK-180 fix: ST_Union…») — под «подробнее».
  const techReason = isTechnicalReason(ev.reason);
  return (
    <li className="relative pl-4 pb-3 border-l border-border last:border-l-transparent">
      <span
        aria-hidden
        className="absolute -left-[5px] top-1 h-2.5 w-2.5 rounded-full bg-primary"
      />
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span className="tabular-nums">{fmtWhen(ev.when)}</span>
        {ev.who_name && <span>· {ev.who_name}</span>}
        <Badge variant="outline" className="text-xs py-0">
          {JOURNAL_TYPE_LABELS[ev.type] ?? ev.type}
        </Badge>
      </div>
      <div className="text-sm font-medium mt-1">{what}</div>
      {(before || after) && (
        <div className="text-sm text-muted-foreground mt-1 flex flex-wrap items-center gap-1">
          {before && <span className="line-through opacity-70">{before}</span>}
          {before && after && <ArrowRight className="h-3.5 w-3.5" />}
          {after && <span className="text-foreground">{after}</span>}
        </div>
      )}
      {ev.reason && !techReason && (
        <div className="text-xs text-muted-foreground mt-1">
          Причина: {ev.reason}
        </div>
      )}
      {ev.reason && techReason && (
        <div className="mt-1">
          <button
            type="button"
            onClick={() => setReasonOpen((v) => !v)}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            aria-expanded={reasonOpen}
          >
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 transition-transform',
                reasonOpen && 'rotate-180',
              )}
            />
            Подробнее
          </button>
          {reasonOpen && (
            <div className="mt-1 text-xs text-muted-foreground/80 break-words">
              {ev.reason}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export function FieldJournalTab({ fieldId }: Props) {
  const [filter, setFilter] = useState<string | null>(null);
  const journal = useJournal(fieldId, filter);
  // Подписи состояний поля — из справочника; пока грузится, в тексте остаётся
  // общее «Пар» (журнал из-за справочника не блокируем).
  const fallowLabel = useFieldStateLabel();

  const [asOfDate, setAsOfDate] = useState<string>('');
  const [snapshot, setSnapshot] = useState<FieldSnapshot | null>(null);
  const [snapLoading, setSnapLoading] = useState(false);

  async function loadSnapshot() {
    if (!asOfDate) {
      toast.error('Выберите дату');
      return;
    }
    setSnapLoading(true);
    try {
      const snap = await fetchFieldSnapshot(fieldId, asOfDate);
      setSnapshot(snap);
    } catch (err) {
      if (err instanceof ApiError) toast.error(err.detail);
      else toast.error('Не удалось получить снимок поля');
    } finally {
      setSnapLoading(false);
    }
  }

  return (
    <div className="space-y-3 py-3">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Журнал поля</CardTitle>
          <p className="text-sm text-muted-foreground">
            Единая лента изменений: что · когда · кто · было → стало.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Фильтры + as-of */}
          <div className="flex flex-wrap items-center gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.label}
                type="button"
                onClick={() => setFilter(f.value)}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs transition-colors',
                  filter === f.value
                    ? 'border-primary bg-primary/10 text-primary font-medium'
                    : 'border-border text-muted-foreground hover:bg-accent',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-end gap-2 rounded-md border border-border p-3 bg-muted/30">
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <CalendarClock className="h-3.5 w-3.5" />
                Показать поле на дату
              </div>
              <Input
                type="date"
                value={asOfDate}
                onChange={(e) => setAsOfDate(e.target.value)}
                className="w-44"
              />
            </div>
            <Button size="sm" variant="outline" onClick={loadSnapshot} disabled={snapLoading}>
              {snapLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Показать
            </Button>
            {snapshot && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSnapshot(null)}
                className="text-muted-foreground"
              >
                Сбросить снимок
              </Button>
            )}
          </div>

          {snapshot && (
            <Alert>
              <CalendarClock className="h-4 w-4" />
              <AlertDescription>
                <div className="font-medium text-foreground">
                  Снимок на {snapshot.as_of}
                </div>
                <div className="text-sm">
                  Имя: {snapshot.name ?? '—'}
                  {snapshot.contour?.area_ha != null &&
                    ` · площадь ${snapshot.contour.area_ha.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} га`}
                </div>
                {snapshot.occupancy.length > 0 && (
                  <div className="text-sm mt-1">
                    Посевы:{' '}
                    {snapshot.occupancy
                      .map((o) =>
                        o.occupancy_type === 'fallow'
                          ? fieldStateLabel(o.fallow_kind, fallowLabel)
                          : (o.crop_name ?? 'культура'),
                      )
                      .join(', ')}
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Лента */}
          {journal.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : journal.isError ? (
            <Alert variant="destructive">
              <AlertDescription>Не удалось загрузить журнал.</AlertDescription>
            </Alert>
          ) : journal.data && journal.data.length > 0 ? (
            <ol className="mt-1">
              {journal.data.map((ev) => (
                <EventRow key={ev.id} ev={ev} fallowLabel={fallowLabel} />
              ))}
            </ol>
          ) : (
            <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              {filter ? 'Событий этого типа пока нет.' : 'Журнал пока пуст.'}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
